import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { PageHeader, formatDateTime } from "@/components/Shared";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Play, Square, Camera, X, Image as ImageIcon, MapPin, CalendarPlus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import AbsenceDialog, { AbsenceList } from "@/components/AbsenceDialog";

async function resizeToDataUrl(file, maxSize = 720, quality = 0.78) {
  const reader = new FileReader();
  const dataUrl = await new Promise((res, rej) => {
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = dataUrl;
  });
  const ratio = Math.min(1, maxSize / Math.max(img.width, img.height));
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  canvas.getContext("2d").drawImage(img, 0, 0, w, h);
  return canvas.toDataURL("image/jpeg", quality);
}

function formatHM(minutes) {
  if (!minutes || minutes < 0) return "0:00";
  const total = Math.round(minutes);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function useTick(intervalMs = 1000, active = true) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => setN((x) => x + 1), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs, active]);
  return n;
}

function elapsedWorkingMinutes(shift) {
  if (!shift) return 0;
  const start = new Date(shift.start_at).getTime();
  const end = shift.end_at ? new Date(shift.end_at).getTime() : Date.now();
  let total = (end - start) / 60000;
  (shift.breaks || []).forEach((b) => {
    if (!b.start_at) return;
    const bs = new Date(b.start_at).getTime();
    const be = b.end_at ? new Date(b.end_at).getTime() : end;
    total -= Math.max(0, (be - bs) / 60000);
  });
  return Math.max(0, total);
}

export default function Attendance() {
  const { user } = useAuth();
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [selfie, setSelfie] = useState("");
  const [chosenBranch, setChosenBranch] = useState(user.branch_id || "");
  const [preview, setPreview] = useState(null);
  const [absences, setAbsences] = useState([]);
  const [absenceOpen, setAbsenceOpen] = useState(false);
  const camRef = useRef(null);

  useTick(30000, true); // re-render every 30s to update live timers

  const load = async () => {
    const [a, b, ab] = await Promise.all([
      api.get("/attendance/me"),
      api.get("/branches"),
      api.get("/attendance/absences", { params: { user_id: user.id } }),
    ]);
    setActive(a.data.active);
    setHistory(a.data.history);
    setBranches(b.data);
    setAbsences(ab.data);
  };

  useEffect(() => { load(); }, []);

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  const handleStartClick = () => {
    if (!chosenBranch && user.branch_id) setChosenBranch(user.branch_id);
    if (!chosenBranch && !user.branch_id && branches.length > 0) {
      setChosenBranch(branches[0].id);
    }
    setSelfie("");
    setStartOpen(true);
  };

  const handleSelfie = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const data = await resizeToDataUrl(f);
      setSelfie(data);
    } catch {
      toast.error("Foto se nepodařilo zpracovat");
    } finally {
      e.target.value = "";
    }
  };

  const submitStart = async () => {
    if (!chosenBranch) return toast.error("Vyberte pobočku");
    if (!selfie) return toast.error("Pořiďte selfie");
    setBusy(true);
    try {
      await api.post("/attendance/start", { branch_id: chosenBranch, selfie });
      toast.success("Směna zahájena");
      setStartOpen(false);
      setSelfie("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  const callShift = async (path, okMsg) => {
    setBusy(true);
    try {
      await api.post(path);
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  const liveMinutes = elapsedWorkingMinutes(active);

  return (
    <div data-testid="attendance-page">
      <PageHeader
        eyebrow="Můj den"
        title="Docházka"
        description="Zahajte směnu a po skončení ji ukončete. Při zahájení se pořizuje selfie pro ověření."
      />

      {/* Active shift card OR start button */}
      <div className="card-cafe p-5 sm:p-7 mb-6" data-testid="attendance-status-card">
        {active ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="label-eyebrow text-[#376A4D]">Probíhající směna</div>
                <div className="mt-1 flex items-center gap-2 text-sm text-[#57534E]">
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium text-[#1C1917]">{branchName(active.branch_id)}</span>
                  <span>·</span>
                  <span>Začátek {formatDateTime(active.start_at)}</span>
                </div>
              </div>
              {active.start_selfie && (
                <button
                  onClick={() => setPreview(active.start_selfie)}
                  className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#376A4D] shrink-0"
                  title="Selfie ze začátku směny"
                >
                  <img src={active.start_selfie} alt="" className="w-full h-full object-cover" />
                </button>
              )}
            </div>

            <div className="text-center py-4">
              <div className="label-eyebrow mb-2">Odpracováno</div>
              <div
                className="text-5xl sm:text-6xl font-semibold tracking-tight"
                style={{ fontFamily: "Outfit", color: "#376A4D" }}
                data-testid="attendance-live-timer"
              >
                {formatHM(liveMinutes)}
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3">
              <button
                data-testid="shift-end-btn"
                onClick={() => callShift("/attendance/end", "Směna ukončena")}
                disabled={busy}
                className="px-4 py-4 rounded-xl bg-[#9E4747] text-white font-medium hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                <Square className="w-5 h-5" /> Ukončit směnu
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center py-6 sm:py-10">
            <div className="label-eyebrow mb-3">Nemáte aktivní směnu</div>
            <button
              data-testid="shift-start-btn"
              onClick={handleStartClick}
              disabled={busy || branches.length === 0}
              className="inline-flex items-center gap-3 px-7 py-4 rounded-2xl bg-[#376A4D] text-white text-lg font-medium hover:opacity-90 disabled:opacity-50"
            >
              <Play className="w-6 h-6" /> Začít směnu
            </button>
            {branches.length === 0 && (
              <p className="text-xs text-[#9E4747] mt-2">Nejsou žádné pobočky.</p>
            )}
          </div>
        )}
      </div>

      {/* History */}
      <div className="card-cafe p-5 sm:p-6">
        <div className="label-eyebrow mb-3">Historie směn</div>
        {history.length === 0 ? (
          <div className="text-sm text-[#57534E] py-6 text-center">Zatím žádné dokončené směny.</div>
        ) : (
          <ul className="space-y-3" data-testid="attendance-history-list">
            {history.map((s) => (
              <li key={s.id} className="border border-[#E5E1D8] rounded-md p-3 flex items-start gap-3">
                {s.start_selfie ? (
                  <button
                    onClick={() => setPreview(s.start_selfie)}
                    className="shrink-0 w-12 h-12 rounded-md overflow-hidden border border-[#E5E1D8]"
                  >
                    <img src={s.start_selfie} alt="" className="w-full h-full object-cover" />
                  </button>
                ) : (
                  <div className="shrink-0 w-12 h-12 rounded-md border border-dashed border-[#E5E1D8] grid place-items-center">
                    <ImageIcon className="w-4 h-4 text-[#57534E]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">{formatDateTime(s.start_at)}</span>
                    <span className="text-[#57534E]">→</span>
                    <span>{s.end_at ? formatDateTime(s.end_at) : "—"}</span>
                    {s.status === "auto_closed" && (
                      <span className="text-xs text-[#9E4747] font-medium ml-2">auto-uzavřeno</span>
                    )}
                  </div>
                  <div className="text-sm text-[#57534E] mt-0.5">
                    {branchName(s.branch_id)}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold mono" data-testid={`shift-total-${s.id}`}>{formatHM(s.total_minutes)}</div>
                  {s.weekend_minutes > 0 && (
                    <div className="text-xs text-[#C29329]">+ vík. {formatHM(s.weekend_minutes)}</div>
                  )}
                  {s.holiday_minutes > 0 && (
                    <div className="text-xs text-[#9E4747]">+ svát. {formatHM(s.holiday_minutes)}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Absences (vacation / doctor / sick) */}
      <div className="card-cafe p-5 sm:p-7 mb-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="text-lg font-semibold" style={{ fontFamily: "Outfit" }}>
              Moje nepřítomnosti
            </h3>
            <div className="text-xs text-[#57534E] mt-0.5">
              Dovolená, lékař, nemoc. Vede se samostatně — nezapočítává se k odpracovaným hodinám.
            </div>
          </div>
          <button
            data-testid="absence-new-btn"
            onClick={() => setAbsenceOpen(true)}
            className="btn-primary inline-flex items-center gap-1.5 text-sm"
          >
            <CalendarPlus className="w-4 h-4" /> Přidat nepřítomnost
          </button>
        </div>
        <AbsenceList
          items={absences}
          onDelete={async (a) => {
            try {
              await api.delete(`/attendance/absences/${a.id}`);
              toast.success("Záznam smazán");
              await load();
            } catch (e) {
              toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
            }
          }}
          emptyText="Zatím žádné záznamy."
        />
      </div>

      <AbsenceDialog
        open={absenceOpen}
        onClose={() => setAbsenceOpen(false)}
        onSaved={load}
      />

      {/* Start shift dialog */}
      <Dialog open={startOpen} onOpenChange={setStartOpen}>
        <DialogContent className="sm:max-w-md bg-white border-[#E5E1D8]" data-testid="shift-start-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit" }}>Začít směnu</DialogTitle>
            <DialogDescription>
              Vyberte pobočku a pořiďte selfie pro ověření.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <label className="block">
              <div className="text-sm font-medium mb-1.5">Pobočka</div>
              {user.role === "admin" ? (
                <select
                  data-testid="shift-branch-select"
                  className="input-cafe w-full"
                  value={chosenBranch}
                  onChange={(e) => setChosenBranch(e.target.value)}
                >
                  <option value="">— vyberte —</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              ) : (
                <div className="px-3 py-2 rounded-md border border-[#E5E1D8] bg-[#F9F6F0] text-sm font-medium">
                  {branchName(chosenBranch || user.branch_id) || "—"}
                </div>
              )}
            </label>
            <div>
              <div className="text-sm font-medium mb-1.5">Selfie (povinné)</div>
              {selfie ? (
                <div className="relative">
                  <img src={selfie} alt="" className="w-full max-h-72 object-contain rounded-md border border-[#E5E1D8]" />
                  <button
                    onClick={() => setSelfie("")}
                    className="absolute top-2 right-2 bg-white rounded-full p-1.5 border border-[#E5E1D8]"
                    title="Pořídit znovu"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  data-testid="selfie-capture"
                  type="button"
                  onClick={() => camRef.current?.click()}
                  className="w-full px-4 py-6 rounded-xl border-2 border-dashed border-[#E5E1D8] hover:bg-[#F9F6F0] flex flex-col items-center gap-2 text-[#3A2D27]"
                >
                  <Camera className="w-7 h-7" />
                  <span className="font-medium">Pořídit selfie</span>
                  <span className="text-xs text-[#57534E]">Otevře přední kameru</span>
                </button>
              )}
              <input
                ref={camRef}
                type="file"
                accept="image/*"
                capture="user"
                className="hidden"
                onChange={handleSelfie}
              />
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={() => setStartOpen(false)}
              className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
            >
              Zrušit
            </button>
            <button
              data-testid="shift-start-confirm"
              onClick={submitStart}
              disabled={busy || !selfie || !chosenBranch}
              className="btn-primary"
              style={{ background: "#376A4D", color: "white" }}
            >
              {busy ? "Ukládám…" : "Zahájit směnu"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo preview */}
      {preview && (
        <div
          onClick={() => setPreview(null)}
          className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-6 cursor-zoom-out"
        >
          <img src={preview} alt="" className="max-w-full max-h-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
